<script>
import storageService from "@/services/storage.service";
import Layout from "../../layouts/main";
import { pieChart } from "./dashboard-chart";
import axios from "axios";
import AppIcon from "@/components/shared/AppIcon.vue";
import StatCard from "@/components/shared/StatCard.vue";

// Counter tiles; `key` is a data field filled by fetchData().
const STAT_CARDS = [
  {
    key: "Total_Approved_Applications",
    label: "Approved Applications",
    icon: "arrow-down-circle",
    to: "/approved_application",
  },
  {
    key: "Total_Rejected_Applications",
    label: "Rejected Applications",
    icon: "x-circle",
    to: "/rejected_application",
  },
  {
    key: "Total_Incomplete_Applications",
    label: "Incomplete Applications",
    icon: "trending-up",
    to: "/incomplete_application",
  },
];

// Same order as pieChart.series: approved, rejected, incomplete.
const PIE_SLICES = [
  { label: "Approved", color: "#bdf0e0" },
  { label: "Rejected", color: "#fac9de" },
  { label: "Incomplete", color: "#dcf0c2" },
];

export default {
  name: "StaffDashboard",
  components: {
    Layout,
    AppIcon,
    StatCard,
  },

  data() {
    return {
      Total_Approved_Applications: 0,
      Total_Rejected_Applications: 0,
      Total_Incomplete_Applications: 0,
      duration: 2000,
      interval: 50,
      pieChart: pieChart,
      show_pie: false,
      arrItem: [],

      statCards: STAT_CARDS,
      pieSlices: PIE_SLICES,
      countsLoading: true,
    };
  },

  computed: {
    // the chart only makes sense once at least one reviewed application exists
    hasChartData() {
      return (this.pieChart.series || []).reduce((a, b) => a + b, 0) > 0;
    },
    chartOptions() {
      return {
        chart: {
          type: "donut",
          fontFamily: "inherit",
          toolbar: { show: false },
          animations: { speed: 500 },
        },
        labels: this.pieSlices.map((s) => s.label),
        colors: this.pieSlices.map((s) => s.color),
        legend: { show: false },
        stroke: { width: 3, colors: ["#ffffff"] },
        dataLabels: {
          enabled: true,
          formatter: (val) => (val >= 1 ? `${val.toFixed(1)}%` : ""),
          dropShadow: { enabled: false },
          style: {
            fontSize: "13px",
            fontWeight: 700,
            colors: this.pieSlices.map(() => "#1d2a4d"),
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            dataLabels: { minAngleToShowLabel: 8 },
            donut: { size: "46%", labels: { show: false } },
          },
        },
        states: {
          hover: { filter: { type: "lighten", value: 0.04 } },
          active: { filter: { type: "none" } },
        },
        tooltip: {
          theme: "dark",
          fillSeriesColor: false,
          style: { fontSize: "13px" },
          y: {
            formatter: (v) => `${v} application${v === 1 ? "" : "s"}`,
          },
        },
        responsive: [{ breakpoint: 600, options: { chart: { height: 260 } } }],
      };
    },
  },

  created() {
    this.arrItem = this.items;
  },
  methods: {
    filterStatus() {
      if (this.selectFilterSort == null) {
        this.arrItem = this.items;
      } else {
        this.arrItem = this.items.filter((item) => {
          return item.statusLog == this.selectFilterSort;
        });
      }
    },
    fetchData() {
      const token = storageService.getItem("accessToken");
      axios
        .get("/applications/staff_stats/", {
          params: { token: token },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const data = response.data;
          const approved = Number(data.total_approved ?? data.Total_Approved_Applications) || 0;
          const rejected = Number(data.total_reject ?? data.Total_Rejected_Applications) || 0;
          const incomplete = Number(data.total_incomplete ?? data.Total_Incomplete_Applications) || 0;
          this.Total_Approved_Applications = approved;
          this.Total_Rejected_Applications = rejected;
          this.Total_Incomplete_Applications = incomplete;

          const total = approved + rejected + incomplete;
          if (total > 0) {
            this.pieChart.series = [approved, rejected, incomplete];
          } else {
            this.pieChart.series = [];
          }
          this.show_pie = true;
          this.countsLoading = false;
        })
        .catch((error) => {
          console.error("Error fetching staff stats, falling back:", error);
          axios
            .get("/applications/all_member_count/", {
              params: { token: token },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
              const data = res.data;
              const approved = Number(data.Total_Approved_Applications) || 0;
              const rejected = Number(data.Total_Rejected_Applications) || 0;
              const incomplete = Number(data.Total_Incomplete_Applications) || 0;
              this.Total_Approved_Applications = approved;
              this.Total_Rejected_Applications = rejected;
              this.Total_Incomplete_Applications = incomplete;
              const total = approved + rejected + incomplete;
              this.pieChart.series = total > 0 ? [approved, rejected, incomplete] : [];
              this.show_pie = true;
            })
            .catch((err) => {
              console.error(err);
            })
            .finally(() => {
              this.countsLoading = false;
            });
        });
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<template>
  <Layout>
    <section class="sdash">
      <header class="ecu-page-head">
        <div>
          <h1>Dashboard</h1>
          <p class="ecu-page-head__sub">Application stats for the applications you review.</p>
        </div>
      </header>

      <div class="sdash__top">
        <div class="sdash__stats">
          <StatCard
            v-for="(card, idx) in statCards"
            :key="card.key"
            :icon="card.icon"
            :label="card.label"
            :value="$data[card.key]"
            :to="card.to"
            :loading="countsLoading"
            :index="idx"
          />
        </div>

        <div class="ecu-card sdash__chart">
          <h2 class="sdash__chart-title">Application Stats</h2>

          <div v-if="countsLoading" class="sdash__chart-skeleton">
            <span class="ecu-skeleton"></span>
          </div>

          <template v-else-if="hasChartData">
            <div class="sdash__donut">
              <apexchart
                class="apex-charts"
                height="300"
                type="donut"
                dir="ltr"
                :series="pieChart.series"
                :options="chartOptions"
              >
              </apexchart>
              <span class="sdash__donut-hole" aria-hidden="true">
                <AppIcon name="users" :size="38" :stroke="1.7" />
              </span>
            </div>

            <ul class="sdash__legend">
              <li v-for="slice in pieSlices" :key="slice.label">
                <span class="sdash__dot" :style="{ background: slice.color }"></span>
                {{ slice.label }}
              </li>
            </ul>
          </template>

          <div v-else class="ecu-state">
            <span class="ecu-state__icon"><AppIcon name="inbox" :size="26" /></span>
            <p>No reviewed applications yet. Your approved, rejected and incomplete stats will appear here.</p>
          </div>
        </div>
      </div>
    </section>
  </Layout>
</template>

<style scoped>
.sdash {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.sdash .ecu-page-head {
  margin-bottom: 0;
}

.sdash__top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 22px;
  align-items: stretch;
}
.sdash__stats {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-auto-rows: 1fr;
  gap: 14px;
}
.sdash .sdash__chart {
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.sdash__chart-title {
  margin: 0 0 6px;
  text-align: center;
  font-size: var(--ecu-fs-h2);
  font-weight: var(--ecu-fw-heading);
  letter-spacing: -0.01em;
  color: var(--ecu-navy, #0b1f4b);
}
.sdash__donut {
  position: relative;
}
.sdash__donut-hole {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 96px;
  height: 96px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: var(--ecu-orange, #f97316);
  box-shadow: 0 8px 26px rgba(20, 40, 90, 0.14);
  pointer-events: none;
}
.sdash__legend {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 22px;
}
.sdash__legend li {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-ink, #1d2a4d);
}
.sdash__dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(11, 31, 75, 0.06);
}
.sdash__chart-skeleton {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}
.sdash__chart-skeleton .ecu-skeleton {
  width: 250px;
  height: 250px;
  border-radius: 50%;
}

@media (max-width: 991px) {
  .sdash__top {
    grid-template-columns: minmax(0, 1fr);
  }
}
@media (max-width: 575px) {
  .sdash .sdash__chart {
    padding: 18px 14px;
  }
  .sdash__donut-hole {
    width: 76px;
    height: 76px;
  }
}
</style>